import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const AuthContext = createContext(null);
const API_BASE = process.env.REACT_APP_API_URL || "";
const TOKEN_KEY = "qc_admin_token";
const USER_KEY = "qc_admin_user";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem(TOKEN_KEY);
    const savedUser = localStorage.getItem(USER_KEY);
    if (savedToken && savedUser) {
      try {
        const payload = JSON.parse(atob(savedToken.split(".")[1]));
        if (payload.exp && payload.exp * 1000 > Date.now()) {
          setToken(savedToken); setUser(JSON.parse(savedUser));
        } else { clearSession(); }
      } catch { clearSession(); }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const msLeft = payload.exp * 1000 - Date.now();
      if (msLeft <= 0) { logout(); return; }
      const timer = setTimeout(() => logout(), msLeft);
      return () => clearTimeout(timer);
    } catch { logout(); }
  }, [token]);

  const clearSession = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null); setUser(null);
  };

  const login = useCallback(async (username, password) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (res.ok && data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        setToken(data.token); setUser(data.user);
        return { success: true };
      }
      return { success: false, message: data.message || "Invalid credentials", status: data.status };
    } catch {
      return { success: false, message: "Cannot reach server." };
    }
  }, []);

  const register = useCallback(async (formData) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) return { success: true, message: data.message };
      return { success: false, message: data.message || "Registration failed." };
    } catch {
      return { success: false, message: "Cannot reach server." };
    }
  }, []);

  const logout = useCallback(() => { clearSession(); }, []);
  const authFetch = useCallback((url, options = {}) =>
    fetch(url, {
      ...options,
      headers: { "Content-Type": "application/json", ...(options.headers || {}), Authorization: `Bearer ${token}` },
    }), [token]);

  return (
    <AuthContext.Provider value={{ user, token, loading, isAuthenticated: !!token, login, logout, register, authFetch }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}